import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    FlaskConical, Droplets, Thermometer, Calendar, 
    ChevronRight, Loader2, AlertTriangle, CheckCircle,
    Download, Eye, ArrowLeft, Camera, Beaker, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { apiRequest, formatDate } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import AnalysisHistoryChart from '../components/AnalysisHistoryChart';
import AnalysisInsights from '../components/AnalysisInsights';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';

const getStatusBadge = (status) => {
    switch (status) {
        case 'good':
            return <Badge className="bg-emerald-500/10 text-emerald-500 border-0">Excellent</Badge>;
        case 'warning':
            return <Badge className="bg-amber-500/10 text-amber-500 border-0">Attention</Badge>;
        case 'critical':
            return <Badge className="bg-red-500/10 text-red-500 border-0">Critique</Badge>;
        default:
            return <Badge className="bg-primary/10 text-primary border-0">Analysé</Badge>;
    }
};

const getAnalysisTypeLabel = (analysis) => {
    // Check type field first
    const type = analysis?.type;
    
    // Check if it's a photo analysis by looking at various indicators
    if (type === 'photo' || analysis?.treatment_type || analysis?.result?.readings) {
        return { label: 'Analyse Photo', icon: Camera, color: 'text-blue-500', bgColor: 'bg-blue-500/10' };
    }
    
    // Check if it's a SpinTouch analysis
    if (type === 'spintouch' || type === 'spintouch_admin' || analysis?.device_id) {
        return { label: 'SpinTouch LaMotte', icon: Beaker, color: 'text-purple-500', bgColor: 'bg-purple-500/10' };
    }
    
    // Check if it's a manual analysis (has direct ph, chlorine fields without type)
    if (type === 'manual' || (!type && analysis?.ph !== undefined && analysis?.chlorine !== undefined)) {
        return { label: 'Analyse Manuelle', icon: Droplets, color: 'text-cyan-500', bgColor: 'bg-cyan-500/10' };
    }
    
    // Default fallback
    return { label: 'Analyse', icon: FlaskConical, color: 'text-primary', bgColor: 'bg-primary/10' };
};

export default function MyAnalysesPage() {
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const [analyses, setAnalyses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAnalysis, setSelectedAnalysis] = useState(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(null);

    useEffect(() => {
        if (authLoading) return;  // wait for auth check before redirecting
        if (!user) {
            navigate('/auth');
            return;
        }
        loadAnalyses();
    }, [user, authLoading]);

    const loadAnalyses = async () => {
        setLoading(true);
        try {
            const data = await apiRequest('/analysis/history');
            setAnalyses(data);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const viewDetail = (analysis) => {
        setSelectedAnalysis(analysis);
        setDetailDialogOpen(true);
    };

    const downloadPdf = async (analysisId, e) => {
        if (e) {
            e.stopPropagation();
        }
        setDownloadingPdf(analysisId);
        try {
            const API_URL = process.env.REACT_APP_BACKEND_URL;
            const token = localStorage.getItem('auth_token');
            
            const response = await fetch(`${API_URL}/api/analysis/pdf/${analysisId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (!response.ok) {
                throw new Error('Erreur lors du téléchargement');
            }
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            
            // Get filename from Content-Disposition header or use default
            const disposition = response.headers.get('Content-Disposition');
            let filename = `analyse_${analysisId}.pdf`;
            if (disposition && disposition.includes('filename=')) {
                filename = disposition.split('filename=')[1].replace(/"/g, '');
            }
            
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            toast.success('PDF téléchargé avec succès');
        } catch (error) {
            toast.error(error.message || 'Erreur lors du téléchargement du PDF');
        } finally {
            setDownloadingPdf(null);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen pt-20 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-20 pb-12" data-testid="my-analyses-page">
            {/* Detail Dialog */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="analysis-detail-description">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FlaskConical className="h-5 w-5 text-primary" />
                            Détail de l&apos;analyse
                        </DialogTitle>
                    </DialogHeader>
                    <div id="analysis-detail-description" className="sr-only">
                        Détails complets de l&apos;analyse d&apos;eau incluant les mesures et recommandations
                    </div>
                    {selectedAnalysis && (
                        <div className="space-y-6 py-4">
                            {/* Type and Status */}
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Badge variant="outline" className={getAnalysisTypeLabel(selectedAnalysis).bgColor}>
                                        {getAnalysisTypeLabel(selectedAnalysis).label}
                                    </Badge>
                                    {getStatusBadge(selectedAnalysis.expert_analysis?.status || selectedAnalysis.results?.overall)}
                                </div>
                                <span className="text-sm text-muted-foreground">
                                    {formatDate(selectedAnalysis.created_at)}
                                </span>
                            </div>
                            
                            {/* Temperature */}
                            {selectedAnalysis.temperature && (
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <Thermometer className="h-4 w-4" />
                                    <span>Température de l&apos;eau: {selectedAnalysis.temperature}°C</span>
                                </div>
                            )}

                            {/* Summary */}
                            {(selectedAnalysis.expert_analysis?.summary || selectedAnalysis.expert_analysis?.diagnostic) && (
                                <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                                    <p className="text-sm">{selectedAnalysis.expert_analysis.summary || selectedAnalysis.expert_analysis.diagnostic}</p>
                                </div>
                            )}

                            {/* Measurements */}
                            <div>
                                <h4 className="font-semibold mb-3">Paramètres mesurés</h4>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    {/* Handle both formats: measurements object and direct fields */}
                                    {(selectedAnalysis.measurements?.ph ?? selectedAnalysis.ph) !== undefined && (
                                        <div className="p-3 rounded-xl bg-muted/50 text-center">
                                            <p className="text-xs text-muted-foreground">pH</p>
                                            <p className="text-xl font-bold">{selectedAnalysis.measurements?.ph ?? selectedAnalysis.ph}</p>
                                        </div>
                                    )}
                                    {(selectedAnalysis.measurements?.chlorine ?? selectedAnalysis.chlorine) !== undefined && (
                                        <div className="p-3 rounded-xl bg-muted/50 text-center">
                                            <p className="text-xs text-muted-foreground">Chlore</p>
                                            <p className="text-xl font-bold">{selectedAnalysis.measurements?.chlorine ?? selectedAnalysis.chlorine} <span className="text-sm">ppm</span></p>
                                        </div>
                                    )}
                                    {(selectedAnalysis.measurements?.alkalinity ?? selectedAnalysis.alkalinity) !== undefined && (
                                        <div className="p-3 rounded-xl bg-muted/50 text-center">
                                            <p className="text-xs text-muted-foreground">TAC</p>
                                            <p className="text-xl font-bold">{selectedAnalysis.measurements?.alkalinity ?? selectedAnalysis.alkalinity} <span className="text-sm">ppm</span></p>
                                        </div>
                                    )}
                                    {(selectedAnalysis.measurements?.stabilizer ?? selectedAnalysis.stabilizer) !== undefined && (
                                        <div className="p-3 rounded-xl bg-muted/50 text-center">
                                            <p className="text-xs text-muted-foreground">Stabilisant</p>
                                            <p className="text-xl font-bold">{selectedAnalysis.measurements?.stabilizer ?? selectedAnalysis.stabilizer} <span className="text-sm">ppm</span></p>
                                        </div>
                                    )}
                                    {((selectedAnalysis.measurements?.calcium ?? selectedAnalysis.calcium) > 0) && (
                                        <div className="p-3 rounded-xl bg-muted/50 text-center">
                                            <p className="text-xs text-muted-foreground">Calcaire TH</p>
                                            <p className="text-xl font-bold">{selectedAnalysis.measurements?.calcium ?? selectedAnalysis.calcium} <span className="text-sm">ppm</span></p>
                                        </div>
                                    )}
                                    {(selectedAnalysis.measurements?.salt > 0) && (
                                        <div className="p-3 rounded-xl bg-muted/50 text-center">
                                            <p className="text-xs text-muted-foreground">Sel</p>
                                            <p className="text-xl font-bold">{selectedAnalysis.measurements.salt} <span className="text-sm">g/L</span></p>
                                        </div>
                                    )}
                                    {(selectedAnalysis.measurements?.iron > 0) && (
                                        <div className="p-3 rounded-xl bg-muted/50 text-center">
                                            <p className="text-xs text-muted-foreground">Fer</p>
                                            <p className="text-xl font-bold">{selectedAnalysis.measurements.iron} <span className="text-sm">ppm</span></p>
                                        </div>
                                    )}
                                    {(selectedAnalysis.measurements?.phosphate > 0) && (
                                        <div className="p-3 rounded-xl bg-muted/50 text-center">
                                            <p className="text-xs text-muted-foreground">Phosphate</p>
                                            <p className="text-xl font-bold">{selectedAnalysis.measurements.phosphate} <span className="text-sm">ppm</span></p>
                                        </div>
                                    )}
                                    {(selectedAnalysis.measurements?.copper > 0) && (
                                        <div className="p-3 rounded-xl bg-muted/50 text-center">
                                            <p className="text-xs text-muted-foreground">Cuivre</p>
                                            <p className="text-xl font-bold">{selectedAnalysis.measurements.copper} <span className="text-sm">ppm</span></p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Recommendations */}
                            {(selectedAnalysis.expert_analysis?.treatments?.length > 0 || selectedAnalysis.recommendations?.length > 0) && (
                                <div>
                                    <h4 className="font-semibold mb-3">Produits recommandés</h4>
                                    <div className="space-y-2">
                                        {(selectedAnalysis.expert_analysis?.treatments || selectedAnalysis.recommendations || []).map((treatment, idx) => (
                                            <div key={idx} className="p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
                                                <p className="font-medium">{treatment.product_name || treatment.product || treatment.name}</p>
                                                {(treatment.quantity || treatment.dosage) && (
                                                    <p className="text-sm text-muted-foreground">{treatment.quantity || treatment.dosage}</p>
                                                )}
                                                {(treatment.instructions || treatment.reason) && (
                                                    <p className="text-xs text-muted-foreground mt-1">{treatment.instructions || treatment.reason}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Notes */}
                            {(selectedAnalysis.admin_notes || selectedAnalysis.notes) && (
                                <div>
                                    <h4 className="font-semibold mb-2">Notes du technicien</h4>
                                    <p className="text-sm text-muted-foreground p-3 rounded-xl bg-muted/50">
                                        {selectedAnalysis.admin_notes || selectedAnalysis.notes}
                                    </p>
                                </div>
                            )}
                            
                            {/* Download PDF Button */}
                            <div className="pt-4 border-t">
                                <Button 
                                    onClick={() => downloadPdf(selectedAnalysis.analysis_id)}
                                    disabled={downloadingPdf === selectedAnalysis.analysis_id}
                                    className="w-full rounded-full"
                                    data-testid="download-pdf-button"
                                >
                                    {downloadingPdf === selectedAnalysis.analysis_id ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <FileText className="h-4 w-4 mr-2" />
                                    )}
                                    Télécharger le rapport PDF
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <Button 
                        variant="ghost" 
                        onClick={() => navigate(-1)}
                        className="mb-4 -ml-2"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Retour
                    </Button>
                    <div className="flex items-center gap-3">
                        <FlaskConical className="h-10 w-10 text-primary" />
                        <div>
                            <h1 className="font-heading text-3xl sm:text-4xl font-bold">Mes Analyses</h1>
                            <p className="text-muted-foreground">Historique de vos analyses d&apos;eau</p>
                        </div>
                    </div>
                </div>

                {/* Analyses List */}
                {analyses.length === 0 ? (
                    <Card className="glass rounded-3xl">
                        <CardContent className="py-16 text-center">
                            <FlaskConical className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                            <h3 className="font-semibold text-xl mb-2">Aucune analyse</h3>
                            <p className="text-muted-foreground mb-6">
                                Vous n&apos;avez pas encore d&apos;analyse d&apos;eau enregistrée.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <Button onClick={() => navigate('/analysis')} className="rounded-full">
                                    <Droplets className="h-4 w-4 mr-2" />
                                    Faire une analyse manuelle
                                </Button>
                                <Button variant="outline" onClick={() => navigate('/analysis/photo')} className="rounded-full">
                                    <Eye className="h-4 w-4 mr-2" />
                                    Analyse par photo
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-6">
                        {/* History chart */}
                        <AnalysisHistoryChart />

                        {/* Smart insights */}
                        <AnalysisInsights />

                        {/* List header */}
                        <div>
                            <h2 className="font-heading text-xl font-semibold mb-3">Historique détaillé</h2>
                        </div>
                        {analyses.map((analysis) => {
                            const typeInfo = getAnalysisTypeLabel(analysis);
                            const TypeIcon = typeInfo.icon;
                            
                            // Get measurements from either format
                            const ph = analysis.measurements?.ph ?? analysis.ph;
                            const chlorine = analysis.measurements?.chlorine ?? analysis.chlorine;
                            const temp = analysis.temperature;
                            
                            return (
                                <Card 
                                    key={analysis.analysis_id} 
                                    className="glass rounded-2xl hover:shadow-lg transition-shadow cursor-pointer"
                                    onClick={() => viewDetail(analysis)}
                                    data-testid={`analysis-${analysis.analysis_id}`}
                                >
                                    <CardContent className="p-4 sm:p-6">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex items-start gap-4">
                                                <div className={`h-12 w-12 rounded-full ${typeInfo.bgColor} flex items-center justify-center flex-shrink-0`}>
                                                    <TypeIcon className={`h-6 w-6 ${typeInfo.color}`} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                                        <h3 className="font-semibold">{typeInfo.label}</h3>
                                                        {getStatusBadge(analysis.expert_analysis?.status || analysis.results?.overall)}
                                                    </div>
                                                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                                                        <Calendar className="h-3 w-3" />
                                                        {formatDate(analysis.created_at)}
                                                    </p>
                                                    
                                                    {/* Quick stats */}
                                                    <div className="flex gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                                                        {ph !== undefined && (
                                                            <span>pH: <strong>{ph}</strong></span>
                                                        )}
                                                        {chlorine !== undefined && (
                                                            <span>Cl: <strong>{chlorine}</strong> ppm</span>
                                                        )}
                                                        {temp && (
                                                            <span className="flex items-center gap-1">
                                                                <Thermometer className="h-3 w-3" />
                                                                {temp}°C
                                                            </span>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Summary preview */}
                                                    {(analysis.expert_analysis?.summary || analysis.expert_analysis?.diagnostic) && (
                                                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                                                            {analysis.expert_analysis.summary || analysis.expert_analysis.diagnostic}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={(e) => downloadPdf(analysis.analysis_id, e)}
                                                    disabled={downloadingPdf === analysis.analysis_id}
                                                    className="rounded-full h-8 w-8"
                                                    data-testid={`download-pdf-${analysis.analysis_id}`}
                                                >
                                                    {downloadingPdf === analysis.analysis_id ? (
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                    ) : (
                                                        <Download className="h-4 w-4" />
                                                    )}
                                                </Button>
                                                <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {/* CTA */}
                <Card className="glass rounded-3xl mt-8">
                    <CardContent className="p-6 text-center">
                        <p className="text-muted-foreground mb-4">
                            Besoin d&apos;une analyse professionnelle ?
                        </p>
                        <Button onClick={() => navigate('/appointment')} variant="outline" className="rounded-full">
                            <Calendar className="h-4 w-4 mr-2" />
                            Prendre rendez-vous en magasin
                        </Button>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
